<template>
  <div class="flex h-screen bg-gray-50 overflow-hidden font-sans">
    <transition
      enter-active-class="transition-opacity duration-300 ease-linear"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-300 ease-linear"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isSidebarOpen"
        @click="isSidebarOpen = false"
        class="fixed inset-0 bg-slate-900/50 z-40 xl:hidden"
      ></div>
    </transition>

    <aside
      class="w-64 bg-slate-900 text-slate-300 flex flex-col shrink-0 fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out xl:static xl:translate-x-0"
      :class="isSidebarOpen ? 'translate-x-0' : '-translate-x-full'"
    >
      <div
        class="h-16 flex items-center px-6 text-white font-bold text-xl border-b border-slate-800"
      >
        <span class="bg-blue-600 p-1.5 rounded-lg mr-2">
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M13 10V3L4 14h7v7l9-11h-7z"
            />
          </svg>
        </span>
        My SaaS
      </div>

      <nav class="flex-1 p-4 space-y-2 overflow-y-auto">
        <div class="group/nav" v-if="FEATURE_FLAGS.analytics && hasAnyAnalytics">
          <div
            class="flex items-center rounded-lg transition-all duration-200"
            :class="isAnalyticRouteActive() ? 'bg-slate-800 text-white' : 'hover:bg-slate-800/50'"
          >
            <button
              @click="handleMainAnalyticClick"
              class="flex-1 flex items-center gap-3 px-4 py-2.5 text-left transition-colors"
            >
              <svg
                class="w-5 h-5 transition-colors"
                :class="isAnalyticRouteActive() ? 'text-blue-400' : 'text-slate-400'"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              <span class="font-medium">Analytics</span>
            </button>

            <button
              @click.stop="isAnalyticsOpen = !isAnalyticsOpen"
              class="px-3 py-2.5 border-l border-slate-800/50 hover:text-white transition-colors"
              aria-label="Toggle sub-menu"
            >
              <svg
                class="w-4 h-4 transition-transform duration-300"
                :class="{ 'rotate-180': isAnalyticsOpen }"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>

          <transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="transform -translate-y-2 opacity-0"
            enter-to-class="transform translate-y-0 opacity-100"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="transform translate-y-0 opacity-100"
            leave-to-class="transform -translate-y-2 opacity-0"
          >
            <div v-show="isAnalyticsOpen" class="mt-1 ml-4 border-l border-slate-800 space-y-1">
              <RouterLink
                v-if="hasSalesAnalytics"
                :to="{ name: 'SalesAnalytics' }"
                class="block ml-4 px-4 py-2 text-sm rounded-md transition-colors"
                active-class="bg-blue-600/10 text-blue-400 font-semibold"
                inactive-class="text-slate-400 hover:text-white"
              >
                Sales Analytics
              </RouterLink>
              <RouterLink
                v-if="hasProductionAnalytics"
                :to="{ name: 'ProductionAnalytics' }"
                class="block ml-4 px-4 py-2 text-sm rounded-md transition-colors"
                active-class="bg-blue-600/10 text-blue-400 font-semibold"
                inactive-class="text-slate-400 hover:text-white"
              >
                Production Analytics
              </RouterLink>
            </div>
          </transition>
        </div>
        <div class="group/nav">
          <div
            class="flex items-center rounded-lg transition-all duration-200"
            :class="isContactRouteActive() ? 'bg-slate-800 text-white' : 'hover:bg-slate-800/50'"
          >
            <button
              @click="handleMainContactClick"
              class="flex-1 flex items-center gap-3 px-4 py-2.5 text-left transition-colors"
            >
              <svg
                class="w-5 h-5 transition-colors"
                :class="isContactRouteActive() ? 'text-blue-400' : 'text-slate-400'"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <span class="font-medium">Contacts</span>
            </button>

            <button
              @click.stop="isContactsOpen = !isContactsOpen"
              class="px-3 py-2.5 border-l border-slate-800/50 hover:text-white transition-colors"
              aria-label="Toggle sub-menu"
            >
              <svg
                class="w-4 h-4 transition-transform duration-300"
                :class="{ 'rotate-180': isContactsOpen }"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>

          <transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="transform -translate-y-2 opacity-0"
            enter-to-class="transform translate-y-0 opacity-100"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="transform translate-y-0 opacity-100"
            leave-to-class="transform -translate-y-2 opacity-0"
          >
            <div v-show="isContactsOpen" class="mt-1 ml-4 border-l border-slate-800 space-y-1">
              <RouterLink
                :to="{ name: 'NewLeads' }"
                class="block ml-4 px-4 py-2 text-sm rounded-md transition-colors"
                active-class="bg-blue-600/10 text-blue-400 font-semibold"
                inactive-class="text-slate-400 hover:text-white"
              >
                New Leads
              </RouterLink>

              <RouterLink
                :to="{ name: 'ContactList' }"
                class="block ml-4 px-4 py-2 text-sm rounded-md transition-colors"
                active-class="bg-blue-600/10 text-blue-400 font-semibold"
                inactive-class="text-slate-400 hover:text-white"
              >
                All Contacts
              </RouterLink>
            </div>
          </transition>
        </div>
        <div class="group/nav">
          <div
            class="flex items-center rounded-lg transition-all duration-200"
            :class="
              isAppointmentRouteActive() ? 'bg-slate-800 text-white' : 'hover:bg-slate-800/50'
            "
          >
            <button
              @click="handleMainAppointmentClick"
              class="flex-1 flex items-center gap-3 px-4 py-2.5 text-left transition-colors"
            >
              <svg
                class="w-5 h-5 transition-colors"
                :class="isAppointmentRouteActive() ? 'text-blue-400' : 'text-slate-400'"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span class="font-medium">Appointments</span>
            </button>

            <button
              @click.stop="isAppointmentsOpen = !isAppointmentsOpen"
              class="px-3 py-2.5 border-l border-slate-800/50 hover:text-white transition-colors"
              aria-label="Toggle sub-menu"
            >
              <svg
                class="w-4 h-4 transition-transform duration-300"
                :class="{ 'rotate-180': isAppointmentsOpen }"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>

          <transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="transform -translate-y-2 opacity-0"
            enter-to-class="transform translate-y-0 opacity-100"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="transform translate-y-0 opacity-100"
            leave-to-class="transform -translate-y-2 opacity-0"
          >
            <div v-show="isAppointmentsOpen" class="mt-1 ml-4 border-l border-slate-800 space-y-1">
              <RouterLink
                :to="{ name: 'MyAppointments' }"
                class="block ml-4 px-4 py-2 text-sm rounded-md transition-colors"
                active-class="bg-blue-600/10 text-blue-400 font-semibold"
                inactive-class="text-slate-400 hover:text-white"
              >
                My Appointments
              </RouterLink>
              <RouterLink
                :to="{ name: 'AppointmentBoard' }"
                class="block ml-4 px-4 py-2 text-sm rounded-md transition-colors"
                active-class="bg-blue-600/10 text-blue-400 font-semibold"
                inactive-class="text-slate-400 hover:text-white"
              >
                All Appointments
              </RouterLink>
            </div>
          </transition>
        </div>
        <div class="group/nav">
          <div
            class="flex items-center rounded-lg transition-all duration-200"
            :class="isQuoteRouteActive() ? 'bg-slate-800 text-white' : 'hover:bg-slate-800/50'"
          >
            <button
              @click="handleMainQuoteClick"
              class="flex-1 flex items-center gap-3 px-4 py-2.5 text-left transition-colors"
            >
              <svg
                class="w-5 h-5 transition-colors"
                :class="isQuoteRouteActive() ? 'text-blue-400' : 'text-slate-400'"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <span class="font-medium">Quotes</span>
            </button>

            <button
              @click.stop="isQuotesOpen = !isQuotesOpen"
              class="px-3 py-2.5 border-l border-slate-800/50 hover:text-white transition-colors"
              aria-label="Toggle sub-menu"
            >
              <svg
                class="w-4 h-4 transition-transform duration-300"
                :class="{ 'rotate-180': isQuotesOpen }"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>

          <transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="transform -translate-y-2 opacity-0"
            enter-to-class="transform translate-y-0 opacity-100"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="transform translate-y-0 opacity-100"
            leave-to-class="transform -translate-y-2 opacity-0"
          >
            <div v-show="isQuotesOpen" class="mt-1 ml-4 border-l border-slate-800 space-y-1">
              <RouterLink
                :to="{ name: 'PendingQuotes' }"
                class="block ml-4 px-4 py-2 text-sm rounded-md transition-colors"
                active-class="bg-blue-600/10 text-blue-400 font-semibold"
                inactive-class="text-slate-400 hover:text-white"
              >
                Pending Quotes
              </RouterLink>
              <RouterLink
                :to="{ name: 'MyDraftQuotes' }"
                class="block ml-4 px-4 py-2 text-sm rounded-md transition-colors"
                active-class="bg-blue-600/10 text-blue-400 font-semibold"
                inactive-class="text-slate-400 hover:text-white"
              >
                My Draft Quotes
              </RouterLink>

              <RouterLink
                :to="{ name: 'QuoteList' }"
                class="block ml-4 px-4 py-2 text-sm rounded-md transition-colors"
                active-class="bg-blue-600/10 text-blue-400 font-semibold"
                inactive-class="text-slate-400 hover:text-white"
              >
                All Quotes
              </RouterLink>
            </div>
          </transition>
        </div>
        <div class="group/nav" v-if="FEATURE_FLAGS.jobs">
          <div
            class="flex items-center rounded-lg transition-all duration-200"
            :class="isJobRouteActive() ? 'bg-slate-800 text-white' : 'hover:bg-slate-800/50'"
          >
            <button
              @click="handleMainJobClick"
              class="flex-1 flex items-center gap-3 px-4 py-2.5 text-left transition-colors"
            >
              <svg
                class="w-5 h-5 transition-colors"
                :class="isJobRouteActive() ? 'text-blue-400' : 'text-slate-400'"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                />
              </svg>
              <span class="font-medium">Jobs</span>
            </button>

            <button
              @click.stop="isJobsOpen = !isJobsOpen"
              class="px-3 py-2.5 border-l border-slate-800/50 hover:text-white transition-colors"
              aria-label="Toggle sub-menu"
            >
              <svg
                class="w-4 h-4 transition-transform duration-300"
                :class="{ 'rotate-180': isJobsOpen }"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>

          <transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="transform -translate-y-2 opacity-0"
            enter-to-class="transform translate-y-0 opacity-100"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="transform translate-y-0 opacity-100"
            leave-to-class="transform -translate-y-2 opacity-0"
          >
            <div v-show="isJobsOpen" class="mt-1 ml-4 border-l border-slate-800 space-y-1">
              <RouterLink
                :to="{ name: 'PendingJobs' }"
                class="block ml-4 px-4 py-2 text-sm rounded-md transition-colors"
                active-class="bg-blue-600/10 text-blue-400 font-semibold"
                inactive-class="text-slate-400 hover:text-white"
              >
                Pending Jobs
              </RouterLink>
              <RouterLink
                :to="{ name: 'UpcomingJobs' }"
                class="block ml-4 px-4 py-2 text-sm rounded-md transition-colors"
                :class="
                  isScheduleRouteActive()
                    ? 'bg-blue-600/10 text-blue-400 font-semibold'
                    : ' hover:text-white'
                "
              >
                Schedule
              </RouterLink>
              <RouterLink
                :to="{ name: 'JobList' }"
                class="block ml-4 px-4 py-2 text-sm rounded-md transition-colors"
                active-class="bg-blue-600/10 text-blue-400 font-semibold"
                inactive-class="text-slate-400 hover:text-white"
              >
                All Jobs
              </RouterLink>
            </div>
          </transition>
        </div>
        <div class="group/nav" v-if="FEATURE_FLAGS.financials">
          <div
            class="flex items-center rounded-lg transition-all duration-200"
            :class="isFinancialRouteActive() ? 'bg-slate-800 text-white' : 'hover:bg-slate-800/50'"
          >
            <button
              @click="handleMainFinancialClick"
              class="flex-1 flex items-center gap-3 px-4 py-2.5 text-left transition-colors"
            >
              <svg
                class="w-5 h-5 transition-colors"
                :class="isFinancialRouteActive() ? 'text-blue-400' : 'text-slate-400'"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <span class="font-medium">Financials</span>
            </button>

            <button
              @click.stop="isFinancialsOpen = !isFinancialsOpen"
              class="px-3 py-2.5 border-l border-slate-800/50 hover:text-white transition-colors"
              aria-label="Toggle sub-menu"
            >
              <svg
                class="w-4 h-4 transition-transform duration-300"
                :class="{ 'rotate-180': isFinancialsOpen }"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>

          <transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="transform -translate-y-2 opacity-0"
            enter-to-class="transform translate-y-0 opacity-100"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="transform translate-y-0 opacity-100"
            leave-to-class="transform -translate-y-2 opacity-0"
          >
            <div v-show="isFinancialsOpen" class="mt-1 ml-4 border-l border-slate-800 space-y-1">
              <RouterLink
                :to="{ name: 'UnpaidInvoices' }"
                class="block ml-4 px-4 py-2 text-sm rounded-md transition-colors"
                active-class="bg-blue-600/10 text-blue-400 font-semibold"
                inactive-class="text-slate-400 hover:text-white"
              >
                Unpaid Invoices
              </RouterLink>
              <RouterLink
                :to="{ name: 'InvoiceList' }"
                class="block ml-4 px-4 py-2 text-sm rounded-md transition-colors"
                active-class="bg-blue-600/10 text-blue-400 font-semibold"
                inactive-class="text-slate-400 hover:text-white"
              >
                All Invoices
              </RouterLink>
              <RouterLink
                :to="{ name: 'PaymentList' }"
                class="block ml-4 px-4 py-2 text-sm rounded-md transition-colors"
                active-class="bg-blue-600/10 text-blue-400 font-semibold"
                inactive-class="text-slate-400 hover:text-white"
              >
                Payments
              </RouterLink>
              <RouterLink
                :to="{ name: 'ExpenseList' }"
                class="block ml-4 px-4 py-2 text-sm rounded-md transition-colors"
                active-class="bg-blue-600/10 text-blue-400 font-semibold"
                inactive-class="text-slate-400 hover:text-white"
              >
                Expenses
              </RouterLink>
            </div>
          </transition>
        </div>
        <div class="group/nav" v-if="FEATURE_FLAGS.payroll && canViewPayroll">
          <div
            class="flex items-center rounded-lg transition-all duration-200"
            :class="isPayrollRouteActive() ? 'bg-slate-800 text-white' : 'hover:bg-slate-800/50'"
          >
            <button
              @click="handleMainPayrollClick"
              class="flex-1 flex items-center gap-3 px-4 py-2.5 text-left transition-colors"
            >
              <svg
                class="w-5 h-5 transition-colors"
                :class="isPayrollRouteActive() ? 'text-blue-400' : 'text-slate-400'"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 14.25l6-6m4.5-3.493V21.75l-3.75-1.5-3.75 1.5-3.75-1.5-3.75 1.5V4.757c0-1.108.806-2.057 1.907-2.182l9.693-1.103a2.001 2.001 0 012.182 1.907zM9 7.5h.008v.008H9V7.5zm6 9h.008v.008H15v-.008z"
                />
              </svg>
              <span class="font-medium">Payroll</span>
            </button>

            <button
              @click.stop="isPayrollOpen = !isPayrollOpen"
              class="px-3 py-2.5 border-l border-slate-800/50 hover:text-white transition-colors"
              aria-label="Toggle sub-menu"
            >
              <svg
                class="w-4 h-4 transition-transform duration-300"
                :class="{ 'rotate-180': isPayrollOpen }"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>

          <transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="transform -translate-y-2 opacity-0"
            enter-to-class="transform translate-y-0 opacity-100"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="transform translate-y-0 opacity-100"
            leave-to-class="transform -translate-y-2 opacity-0"
          >
            <div v-show="isPayrollOpen" class="mt-1 ml-4 border-l border-slate-800 space-y-1">
              <RouterLink
                :to="{ name: 'PayrollRunList' }"
                class="block ml-4 px-4 py-2 text-sm rounded-md transition-colors"
                active-class="bg-blue-600/10 text-blue-400 font-semibold"
                inactive-class="text-slate-400 hover:text-white"
              >
                Payroll
              </RouterLink>
              <RouterLink
                :to="{ name: 'AdminTimeOff' }"
                class="block ml-4 px-4 py-2 text-sm rounded-md transition-colors"
                active-class="bg-blue-600/10 text-blue-400 font-semibold"
                inactive-class="text-slate-400 hover:text-white"
              >
                Time Off Requests
              </RouterLink>
              <RouterLink
                :to="{ name: 'AdminTimeTracking' }"
                class="block ml-4 px-4 py-2 text-sm rounded-md transition-colors"
                active-class="bg-blue-600/10 text-blue-400 font-semibold"
                inactive-class="text-slate-400 hover:text-white"
              >
                Time Tracking
              </RouterLink>
              <RouterLink
                :to="{ name: 'AdminCommissions' }"
                class="block ml-4 px-4 py-2 text-sm rounded-md transition-colors"
                :class="
                  isCommissionRouteActive()
                    ? 'bg-blue-600/10 text-blue-400 font-semibold'
                    : ' hover:text-white'
                "
              >
                Commissions
              </RouterLink>
            </div>
          </transition>
        </div>
        <div class="group/nav" v-if="canViewTimeAndPay">
          <div
            class="flex items-center rounded-lg transition-all duration-200"
            :class="
              isTimeAndCommissionsRouteActive()
                ? 'bg-slate-800 text-white'
                : 'hover:bg-slate-800/50'
            "
          >
            <button
              @click="handleMainTimeAndCommissionsClick"
              class="flex-1 flex items-center gap-3 px-4 py-2.5 text-left transition-colors"
            >
              <svg
                class="w-5 h-5 transition-colors"
                :class="isTimeAndCommissionsRouteActive() ? 'text-blue-400' : 'text-slate-400'"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                  d="M21 12a2.25 2.25 0 0 0-2.25-2.25H15a3 3 0 1 1-6 0H5.25A2.25 2.25 0 0 0 3 12m18 0v6a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 18v-6m18 0V9M3 12V9m18 0a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 9m18 0V6a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 6v3"
                />
              </svg>
              <span class="font-medium">{{
                hasMemberCommissions ? 'Time & Pay' : 'Time Off'
              }}</span>
            </button>

            <button
              @click.stop="isTimeAndCommissionsOpen = !isTimeAndCommissionsOpen"
              class="px-3 py-2.5 border-l border-slate-800/50 hover:text-white transition-colors"
              aria-label="Toggle sub-menu"
            >
              <svg
                class="w-4 h-4 transition-transform duration-300"
                :class="{ 'rotate-180': isTimeAndCommissionsOpen }"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>

          <transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="transform -translate-y-2 opacity-0"
            enter-to-class="transform translate-y-0 opacity-100"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="transform translate-y-0 opacity-100"
            leave-to-class="transform -translate-y-2 opacity-0"
          >
            <div
              v-show="isTimeAndCommissionsOpen"
              class="mt-1 ml-4 border-l border-slate-800 space-y-1"
            >
              <RouterLink
                :to="{ name: 'MemberTimeOff' }"
                class="block ml-4 px-4 py-2 text-sm rounded-md transition-colors"
                active-class="bg-blue-600/10 text-blue-400 font-semibold"
                inactive-class="text-slate-400 hover:text-white"
              >
                My Time Off
              </RouterLink>
              <RouterLink
                v-if="hasMemberCommissions"
                :to="{ name: 'MemberCommissions' }"
                class="block ml-4 px-4 py-2 text-sm rounded-md transition-colors"
                active-class="bg-blue-600/10 text-blue-400 font-semibold"
                inactive-class="text-slate-400 hover:text-white"
              >
                My Commissions
              </RouterLink>
              <RouterLink
                :to="{ name: 'MemberPaystubs' }"
                class="block ml-4 px-4 py-2 text-sm rounded-md transition-colors"
                active-class="bg-blue-600/10 text-blue-400 font-semibold"
                inactive-class="text-slate-400 hover:text-white"
              >
                My Paystubs
              </RouterLink>
            </div>
          </transition>
        </div>
      </nav>
    </aside>

    <div class="flex-1 flex flex-col overflow-hidden min-w-0">
      <header
        class="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-8 shrink-0 shadow-sm z-30 relative"
      >
        <div class="flex items-center gap-3 lg:gap-4">
          <button
            @click="isSidebarOpen = true"
            class="p-2 -ml-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700 rounded-lg xl:hidden transition-colors cursor-pointer"
          >
            <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
          <OrgSwitcher />
        </div>

        <div class="flex items-center gap-4">
          <RouterLink
            v-if="canManageSettings"
            :to="{ name: 'MembershipList' }"
            class="p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-blue-600 transition-colors"
            title="Settings & Configuration"
          >
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </RouterLink>
          <UserButton after-sign-out-url="/auth/sign-in" />
        </div>
      </header>

      <main class="flex-1 overflow-y-auto bg-slate-50 relative">
        <RouterView />
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watchEffect, watch } from 'vue'
import { RouterLink, RouterView, useRouter, useRoute } from 'vue-router'
import { UserButton } from '@clerk/vue'
import OrgSwitcher from '@/modules/auth/organizations/components/OrgSwitcher.vue'
import { FEATURE_FLAGS } from '@/config/featureFlags'
import { useAuth } from '@clerk/vue'
import { useOrganization } from '@clerk/vue'

const { has } = useAuth()

const canManageSettings = computed(() => {
  if (!has.value) return false
  return has.value({ role: 'org:owner' }) || has.value({ role: 'org:admin' })
})

const canViewPayroll = computed(() => {
  if (!has.value) return false
  return has.value({ role: 'org:owner' }) || has.value({ role: 'org:admin' })
})

const canViewTimeAndPay = computed(() => {
  if (!has.value) return false
  return has.value({ role: 'org:member' })
})

const { membership } = useOrganization()
const hasSalesAnalytics = computed(() => {
  if (!membership.value) return false

  const role = membership.value.role
  const analyticsPerms = membership.value?.publicMetadata?.analytic_permissions || []

  if (role === 'org:owner') return true

  return analyticsPerms.includes('sales:own') || analyticsPerms.includes('sales:location')
})

const hasProductionAnalytics = computed(() => {
  if (!membership.value) return false

  const role = membership.value.role
  const analyticsPerms = membership.value?.publicMetadata?.analytic_permissions || []

  if (role === 'org:owner') return true

  return analyticsPerms.includes('production:own') || analyticsPerms.includes('production:location')
})

const hasAnyAnalytics = computed(() => {
  return hasSalesAnalytics.value || hasProductionAnalytics.value
})

const hasMemberCommissions = computed(() => {
  if (!has.value || !membership.value) return false
  const isMember = has.value({ role: 'org:member' })
  const hasCommissionPlan = membership.value?.publicMetadata?.commission_plan || false
  return isMember && hasCommissionPlan
})

const router = useRouter()
const route = useRoute()

// State for Mobile Sidebar
const isSidebarOpen = ref(false)

// State for accordion - Open by default
const isAnalyticsOpen = ref(false)
const isContactsOpen = ref(false)
const isAppointmentsOpen = ref(false)
const isQuotesOpen = ref(false)
const isJobsOpen = ref(false)
const isFinancialsOpen = ref(false)
const isPayrollOpen = ref(false)
const isTimeAndCommissionsOpen = ref(false)

// Helper to check if a route is active

const isContactRouteActive = () => {
  const contactRoutes = ['ContactList', 'NewLeads', 'EditContact', 'CreateContact']
  return contactRoutes.includes(route.name)
}

const isAppointmentRouteActive = () => {
  const appointmentRoutes = [
    'AppointmentList',
    'CreateAppointment',
    'EditAppointment',
    'MyAppointments',
    'AppointmentBoard',
  ]
  return appointmentRoutes.includes(route.name)
}

const isQuoteRouteActive = () => {
  const quoteRoutes = [
    'QuoteList',
    'CreateQuote',
    'EditQuote',
    'MyDraftQuotes',
    'PendingQuotes',
    'PresentQuotes',
  ]
  return quoteRoutes.includes(route.name)
}

const isJobRouteActive = () => {
  const jobRoutes = [
    'JobList',
    'PendingJobs',
    'CreateJob',
    'ViewJobs',
    'ScheduleBoard',
    'UpcomingJobs',
    'ResourceRoster',
  ]
  return jobRoutes.includes(route.name)
}

const isScheduleRouteActive = () => {
  const scheduleRoutes = ['ScheduleBoard', 'UpcomingJobs', 'ResourceRoster']
  return scheduleRoutes.includes(route.name)
}

const isFinancialRouteActive = () => {
  const financialRoutes = [
    'InvoiceList',
    'CreateInvoice',
    'ViewInvoices',
    'PaymentList',
    'CreatePayment',
    'EditPayment',
    'ExpenseList',
    'CreateExpense',
    'EditExpense',
    'UnpaidInvoices',
  ]
  return financialRoutes.includes(route.name)
}

const isAnalyticRouteActive = () => {
  const analyticRoutes = ['SalesAnalytics', 'ProductionAnalytics']
  return analyticRoutes.includes(route.name)
}

const isPayrollRouteActive = () => {
  const payrollRoutes = [
    'PayrollRunList',
    'PayrollRunDisplay',
    'AdminTimeOff',
    'AdminTimeTracking',
    'AdminTimeTrackingList',
    'AdminCommissions',
    'AdminCommissionsList',
    'AdminTimeOffList',
  ]
  return payrollRoutes.includes(route.name)
}

const isCommissionRouteActive = () => {
  const commissionRoutes = ['AdminCommissions', 'AdminCommissionsList']
  return commissionRoutes.includes(route.name)
}

const isTimeAndCommissionsRouteActive = () => {
  const timeAndCommissionsRoutes = ['MemberTimeOff', 'MemberCommissions', 'MemberPaystubs']
  return timeAndCommissionsRoutes.includes(route.name)
}

watchEffect(() => {
  if (isContactRouteActive()) {
    isContactsOpen.value = true
  }

  if (isAppointmentRouteActive()) {
    isAppointmentsOpen.value = true
  }

  if (isAnalyticRouteActive()) {
    isAnalyticsOpen.value = true
  }

  if (isQuoteRouteActive()) {
    isQuotesOpen.value = true
  }

  if (isJobRouteActive()) {
    isJobsOpen.value = true
  }

  if (isFinancialRouteActive()) {
    isFinancialsOpen.value = true
  }

  if (isPayrollRouteActive()) {
    isPayrollOpen.value = true
  }

  if (isTimeAndCommissionsRouteActive()) {
    isTimeAndCommissionsOpen.value = true
  }
})

// 4. Close the mobile sidebar automatically when navigating to a new page
watch(
  () => route.path,
  () => {
    isSidebarOpen.value = false
  },
)

const handleMainAnalyticClick = () => {
  isAnalyticsOpen.value = true
  if (hasSalesAnalytics.value) {
    router.push({ name: 'SalesAnalytics' })
  } else if (hasProductionAnalytics.value) {
    router.push({ name: 'ProductionAnalytics' })
  }
}

const handleMainContactClick = () => {
  isContactsOpen.value = true
  router.push({ name: 'NewLeads' })
}

const handleMainAppointmentClick = () => {
  isAppointmentsOpen.value = true
  router.push({ name: 'MyAppointments' })
}

const handleMainQuoteClick = () => {
  isQuotesOpen.value = true
  router.push({ name: 'PendingQuotes' })
}

const handleMainJobClick = () => {
  isJobsOpen.value = true
  router.push({ name: 'PendingJobs' })
}

const handleMainFinancialClick = () => {
  isFinancialsOpen.value = true
  router.push({ name: 'UnpaidInvoices' })
}

const handleMainPayrollClick = () => {
  isPayrollOpen.value = true
  router.push({ name: 'PayrollRunList' })
}

const handleMainTimeAndCommissionsClick = () => {
  isTimeAndCommissionsOpen.value = true
  router.push({ name: 'MemberTimeOff' })
}
</script>
